import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Users,
  Filter,
  Eye,
  Mail,
  Phone,
  MapPin,
  Clock,
  Building2,
  RefreshCw,
  X,
  AlertCircle,
  CheckCircle2,
  User,
  GraduationCap,
  CreditCard,
  ArrowLeft,
  DollarSign,
  Ticket,
  Pencil,
  FileText,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";
import Loader from "../../../../components/misc/Loader";
import ConfirmPaymentConfirmModal from "./components/ConfirmPaymentConfirmModal";
import EditGroupAttendeeModal from "./components/EditGroupAttendeeModal";
import ConferenceAttendeeFiltersDrawer from "./components/ConferenceAttendeeFiltersDrawer";

const normalizeMotivationAnswers = (answers) => {
  if (!answers) return [];
  let list = answers;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list.filter(
    (item) =>
      item &&
      (item.question || item.answer) &&
      !(typeof item.answer === "string" && item.answer.trim().startsWith("N/A (group registration"))
  );
};

const groupMotivationAnswers = (answers) => {
  const sections = [];
  const bySection = new Map();
  normalizeMotivationAnswers(answers).forEach((item) => {
    const section = item.section || "Other";
    if (!bySection.has(section)) {
      const entry = { section, items: [] };
      bySection.set(section, entry);
      sections.push(entry);
    }
    bySection.get(section).items.push(item);
  });
  return sections;
};

const registrationCharge = (attendee) => {
  const ticket =
    attendee?.ticket_type && typeof attendee.ticket_type === "object"
      ? attendee.ticket_type
      : null;
  const currency = String(ticket?.currency || attendee?.currency || "").trim().toUpperCase();
  const hasTotal =
    attendee?.total_amount !== null &&
    attendee?.total_amount !== undefined &&
    attendee?.total_amount !== "";

  if (hasTotal) {
    const total = parseFloat(attendee.total_amount);
    return { amount: Number.isNaN(total) ? 0 : total, currency };
  }

  const unit = parseFloat(
    ticket?.price ?? attendee?.amount_paid ?? attendee?.payment?.amount ?? attendee?.price
  );
  const unitAmount = Number.isNaN(unit) ? 0 : unit;
  const isGroup =
    attendee?.registration_type === "group" ||
    (Array.isArray(attendee?.attendees) && attendee.attendees.length > 0);
  const quantity = isGroup
    ? attendee.attendees?.length || Number(attendee.group_info?.number_of_attendees) || 1
    : 1;

  return { amount: unitAmount * quantity, currency };
};

const formatMoney = (amount, currency) => {
  const formatted = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const code = String(currency || "").trim().toUpperCase();
  if (code === "USD") return `$${formatted}`;
  if (code === "GHS") return `₵${formatted}`;
  if (!code || code === "UNSPECIFIED") return formatted;
  return `${code} ${formatted}`;
};

const registrationHeadcount = (attendee) => {
  const isGroup =
    attendee?.registration_type === "group" ||
    (Array.isArray(attendee?.attendees) && attendee.attendees.length > 0);
  if (!isGroup) return 1;

  const listed = Array.isArray(attendee?.attendees) ? attendee.attendees.length : 0;
  if (listed > 0) return listed;

  const declared = Number(attendee?.group_info?.number_of_attendees);
  if (!Number.isNaN(declared) && declared > 0) return declared;

  return 1;
};

const formatAmountPaid = (attendee) => {
  const { amount, currency } = registrationCharge(attendee);
  return formatMoney(amount, currency);
};

const isFreePackage = (attendee) => {
  const ticket =
    attendee?.ticket_type && typeof attendee.ticket_type === "object"
      ? attendee.ticket_type
      : null;
  const hasTotal =
    attendee?.total_amount !== null &&
    attendee?.total_amount !== undefined &&
    attendee?.total_amount !== "";
  const raw = hasTotal
    ? attendee.total_amount
    : ticket?.price ?? attendee?.amount_paid ?? attendee?.payment?.amount ?? attendee?.price;

  if (raw === null || raw === undefined || raw === "") return false;
  const amount = parseFloat(raw);
  return !Number.isNaN(amount) && amount === 0;
};

const isSignatureAnswer = (item) =>
  item?.type === "signature" ||
  (typeof item?.answer === "string" && item.answer.startsWith("data:image"));

const MotivationAnswers = ({ answers }) => {
  const sections = groupMotivationAnswers(answers);
  if (sections.length === 0) {
    return <p className="text-sm text-gray-500">No motivational answers recorded.</p>;
  }

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <div key={section.section} className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-blue-700">
            {section.section}
          </p>
          <div className="space-y-3">
            {section.items.map((item, index) => (
              <div key={`${item.question}-${index}`}>
                <p className="text-xs font-medium text-gray-500">{item.question}</p>
                {isSignatureAnswer(item) ? (
                  <img
                    src={item.answer}
                    alt="Signature"
                    className="mt-2 max-h-28 w-full rounded-lg border border-gray-200 bg-white object-contain"
                  />
                ) : (
                  <p className="mt-1 text-sm font-semibold text-gray-900 whitespace-pre-wrap">
                    {item.answer === null || item.answer === undefined || String(item.answer).trim() === ""
                      ? "Not answered"
                      : item.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Conference Attendees Management Component
 */
const ConferenceAttendees = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [registrationTypeFilter, setRegistrationTypeFilter] = useState("all");
  const [ticketTypeFilter, setTicketTypeFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [actionAttendee, setActionAttendee] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
  const [editSavingSlug, setEditSavingSlug] = useState(null);
  const [paymentConfirmingSlug, setPaymentConfirmingSlug] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch events
  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch attendees when event is selected
  useEffect(() => {
    if (selectedEvent) {
      fetchAttendees(selectedEvent.slug || selectedEvent.event_slug);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await eventService.getEvents();
      if (response?.status && response?.data) {
        let eventsData = [];
        if (response.data.status_code === 200 && response.data.data) {
          eventsData = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
        } else if (Array.isArray(response.data)) {
          eventsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        }

        // Filter for only conferences
        const filteredEvents = eventsData.filter((event) => {
          if (!event.event_type || !Array.isArray(event.event_type)) {
            return false;
          }
          return event.event_type.includes("conference");
        });

        setEvents(filteredEvents);
        
        // Check if there's a pre-selected event from navigation state
        const preSelectedEvent = location.state?.event;
        if (preSelectedEvent && filteredEvents.some(e => (e.slug || e.event_slug) === (preSelectedEvent.slug || preSelectedEvent.event_slug))) {
          setSelectedEvent(preSelectedEvent);
        } else if (filteredEvents.length > 0) {
          setSelectedEvent(filteredEvents[0]);
        }
      } else {
        toast.error(response?.message || "Failed to fetch conferences");
        setEvents([]);
      }
    } catch (error) {
      console.error("ERROR: ", error);
      toast.error(
        error.response?.data?.reason ||
          "An error occurred while fetching conferences"
      );
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async (eventSlug) => {
    setAttendeesLoading(true);
    try {
      const response = await eventService.getEventAttendees(eventSlug);
      if (response?.status) {
        let attendeesData = [];
        if (response.data.status_code === 200) {
          // Handle the nested object structure from the API
          const data = response.data.data;
          if (typeof data === "object" && !Array.isArray(data)) {
            // Convert object with numeric keys to array
            attendeesData = Object.values(data);
          } else if (Array.isArray(data)) {
            attendeesData = data;
          }
        } else if (Array.isArray(response.data)) {
          attendeesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          attendeesData = response.data.data;
        }
        setAttendees(attendeesData);
      } else {
        toast.error(response?.message || "Failed to fetch attendees");
        setAttendees([]);
      }
    } catch (error) {
      console.error("ERROR: ", error);
      toast.error(
        error.response?.data?.reason ||
          "An error occurred while fetching attendees"
      );
      setAttendees([]);
    } finally {
      setAttendeesLoading(false);
    }
  };

  // Get payment status for the attendee (used for filters + badges)
  const getPaymentStatus = useCallback((attendee) => {
    const raw =
      attendee?.payment_status ||
      attendee?.payment?.status ||
      attendee?.status ||
      attendee?.registration_status ||
      "";

    if (isFreePackage(attendee)) return "free";

    const status = String(raw).trim().toLowerCase();
    if (!status) return "pending";

    // Be defensive: backend sometimes returns variants like
    // "pending_payment", "pending payment", etc.
    if (status === "pending" || status.includes("pending")) {
      return "pending";
    }

    if (
      status === "paid" ||
      status === "completed" ||
      status === "verified" ||
      status === "approved" ||
      status === "confirmed" ||
      status.includes("paid") ||
      status.includes("completed") ||
      status.includes("verified") ||
      status.includes("approved") ||
      status.includes("confirmed")
    ) {
      return "confirmed";
    }

    // Conservative fallback: unknown statuses should not show as confirmed
    return "pending";
  }, []);

  // Handle both DDMMYYYY (current API) and YYYYMMDD (legacy) DOB formats
  const formatDob = useCallback((dobValue) => {
    if (!dobValue) return "N/A";

    const formatDateParts = (day, month, year) => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const dayText = String(day).padStart(2, "0");
      return `${dayText}-${monthNames[month - 1]}-${year}`;
    };

    const dobString = String(dobValue).trim();
    if (/^\d{8}$/.test(dobString)) {
      const tryBuildDate = (day, month, year) => {
        const parsedDate = new Date(year, month - 1, day);
        const isValidDate =
          parsedDate.getFullYear() === year &&
          parsedDate.getMonth() === month - 1 &&
          parsedDate.getDate() === day;
        return isValidDate ? formatDateParts(day, month, year) : null;
      };

      // Prefer current API format DDMMYYYY first
      const dmyFormatted = tryBuildDate(
        Number(dobString.slice(0, 2)),
        Number(dobString.slice(2, 4)),
        Number(dobString.slice(4, 8))
      );
      if (dmyFormatted) return dmyFormatted;

      // Fallback for legacy YYYYMMDD
      const ymdFormatted = tryBuildDate(
        Number(dobString.slice(6, 8)),
        Number(dobString.slice(4, 6)),
        Number(dobString.slice(0, 4))
      );
      if (ymdFormatted) {
        return ymdFormatted;
      }
    }

    const fallbackDate = new Date(dobString);
    if (Number.isNaN(fallbackDate.getTime())) return dobString;

    return formatDateParts(
      fallbackDate.getDate(),
      fallbackDate.getMonth() + 1,
      fallbackDate.getFullYear()
    );
  }, []);

  const normalizeOptionValue = useCallback((value) => {
    const text = value === null || value === undefined ? "" : String(value);
    return text.trim().toLowerCase();
  }, []);

  const getRegistrationTypeValue = useCallback(
    (attendee) => normalizeOptionValue(attendee?.registration_type),
    [normalizeOptionValue]
  );

  const getTicketTypeNameValue = useCallback(
    (attendee) => normalizeOptionValue(attendee?.ticket_type?.name),
    [normalizeOptionValue]
  );

  const getCountryCandidates = useCallback((attendee) => {
    const candidates = [];
    if (attendee?.group_info?.country) candidates.push(attendee.group_info.country);
    if (attendee?.country_of_residence) candidates.push(attendee.country_of_residence);
    if (attendee?.user?.country_of_residence) candidates.push(attendee.user.country_of_residence);
    if (attendee?.user?.country) candidates.push(attendee.user.country);
    if (Array.isArray(attendee?.attendees)) {
      attendee.attendees.forEach((a) => {
        if (a?.country_of_residence) candidates.push(a.country_of_residence);
      });
    }
    return candidates.filter(Boolean);
  }, []);

  const getLocationCandidates = useCallback((attendee) => {
    const candidates = [];
    if (attendee?.group_info?.city) candidates.push(attendee.group_info.city);
    if (attendee?.departure_city) candidates.push(attendee.departure_city);
    if (attendee?.user?.location) candidates.push(attendee.user.location);
    if (attendee?.user?.region) candidates.push(attendee.user.region);
    if (Array.isArray(attendee?.attendees)) {
      attendee.attendees.forEach((a) => {
        if (a?.departure_city) candidates.push(a.departure_city);
        if (a?.address) candidates.push(a.address);
      });
    }
    return candidates.filter(Boolean);
  }, []);

  const getCountryPrimaryValue = (attendee) => {
    const primary =
      attendee?.group_info?.country ||
      attendee?.country_of_residence ||
      attendee?.user?.country ||
      attendee?.user?.country_of_residence ||
      "";
    return primary || "N/A";
  };

  const getLocationPrimaryValue = (attendee) => {
    const primary =
      attendee?.group_info?.city ||
      attendee?.user?.location ||
      attendee?.departure_city ||
      attendee?.user?.region ||
      "";
    return primary || "N/A";
  };

  const getRegistrationTypeLabel = useCallback(
    (attendee) => {
      const value = getRegistrationTypeValue(attendee);
      if (!value) return "N/A";
      if (value === "single") return "Single";
      if (value === "group") {
        const memberCount =
          (Array.isArray(attendee?.attendees) && attendee.attendees.length) ||
          Number(attendee?.group_info?.number_of_attendees) ||
          0;
        return memberCount ? `Group (${memberCount})` : "Group";
      }
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    [getRegistrationTypeValue]
  );

  const getRegistrationTypeBadgeClasses = useCallback(
    (attendee) => {
      const value = getRegistrationTypeValue(attendee);
      if (value === "single") {
        return "bg-blue-100 text-blue-800 border-blue-200";
      }
      if (value === "group") {
        return "bg-purple-100 text-purple-800 border-purple-200";
      }
      return "bg-gray-100 text-gray-700 border-gray-200";
    },
    [getRegistrationTypeValue]
  );

  const formatTicketPrice = useCallback((price, currency) => {
    const parsedPrice = Number(price);
    if (Number.isNaN(parsedPrice)) return "Price N/A";

    const symbolMap = { GHS: "₵", USD: "$" };
    const normalizedCurrency = String(currency || "").toUpperCase();
    const symbol = symbolMap[normalizedCurrency];
    const formattedAmount = parsedPrice.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (symbol) return `${symbol}${formattedAmount}`;
    if (normalizedCurrency) return `${formattedAmount} ${normalizedCurrency}`;
    return formattedAmount;
  }, []);

  const getTicketTypeDetails = useCallback(
    (attendee) => {
      const ticket = attendee?.ticket_type;
      if (!ticket) return { name: "N/A", price: "Price N/A" };

      if (typeof ticket === "object") {
        return {
          name: ticket.name ? String(ticket.name).trim() : "N/A",
          price: formatTicketPrice(ticket.price, ticket.currency),
        };
      }

      return {
        name: String(ticket).trim() || "N/A",
        price: "Price N/A",
      };
    },
    [formatTicketPrice]
  );

  const registrationTypeOptions = useMemo(() => {
    const map = new Map();
    attendees.forEach((attendee) => {
      const value = getRegistrationTypeValue(attendee);
      if (!value) return;
      if (!map.has(value)) {
        map.set(value, value === "single" ? "Single" : value === "group" ? "Group" : value);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [attendees, getRegistrationTypeValue]);

  const ticketTypeOptions = useMemo(() => {
    const map = new Map();
    attendees.forEach((attendee) => {
      const raw = attendee?.ticket_type?.name;
      const value = normalizeOptionValue(raw);
      if (!value) return;
      if (!map.has(value)) map.set(value, String(raw).trim());
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [attendees, normalizeOptionValue]);

  const countryOptions = useMemo(() => {
    const map = new Map();
    attendees.forEach((attendee) => {
      const raw = getCountryPrimaryValue(attendee);
      const value = normalizeOptionValue(raw);
      if (!value) return;
      if (!map.has(value)) map.set(value, String(raw).trim());
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [attendees, normalizeOptionValue]);

  const locationOptions = useMemo(() => {
    const map = new Map();
    attendees.forEach((attendee) => {
      const raw = getLocationPrimaryValue(attendee);
      const value = normalizeOptionValue(raw);
      if (!value) return;
      if (!map.has(value)) map.set(value, String(raw).trim());
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [attendees, normalizeOptionValue]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalAttendees = attendees.reduce(
      (sum, attendee) => sum + registrationHeadcount(attendee),
      0
    );
    
    // Calculate revenue and payment statuses
    let paidCount = 0;
    let pendingCount = 0;
    const revenueByCurrencyMap = new Map();

    attendees.forEach((attendee) => {
      const paymentStatus = getPaymentStatus(attendee);
      if (paymentStatus === "pending") {
        pendingCount++;
        return;
      }

      paidCount++;
      const { amount, currency } = registrationCharge(attendee);
      const key = currency || "Unspecified";
      revenueByCurrencyMap.set(key, (revenueByCurrencyMap.get(key) || 0) + amount);
    });

    const revenueByCurrency = Array.from(revenueByCurrencyMap.entries())
      .map(([currency, amount]) => ({ currency, amount }))
      .sort((a, b) => a.currency.localeCompare(b.currency));

    return {
      totalAttendees,
      revenueByCurrency,
      paidCount,
      pendingCount,
    };
  }, [attendees, getPaymentStatus]);

  // Filter and search attendees
  const filteredAttendees = useMemo(() => {
    return attendees
      .filter((attendee) => {
        const user = attendee.user || {};
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
        const email = (user.email || "").toLowerCase();
        const phone = (user.phone_number || "").toLowerCase();
        const location = (user.location || "").toLowerCase();

        const matchesSearch =
          fullName.includes(searchTerm.toLowerCase()) ||
          email.includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm.toLowerCase()) ||
          location.includes(searchTerm.toLowerCase());

        const matchesPaymentStatus =
          paymentStatusFilter === "all" ||
          getPaymentStatus(attendee) === paymentStatusFilter;

        const matchesVerification =
          verificationFilter === "all" ||
          (verificationFilter === "verified" &&
            (user.email_verified_at || user.phone_number_verified_at)) ||
          (verificationFilter === "unverified" &&
            !user.email_verified_at &&
            !user.phone_number_verified_at);

        const matchesRegistrationType =
          registrationTypeFilter === "all" ||
          getRegistrationTypeValue(attendee) ===
            normalizeOptionValue(registrationTypeFilter);

        const matchesTicketType =
          ticketTypeFilter === "all" ||
          getTicketTypeNameValue(attendee) ===
            normalizeOptionValue(ticketTypeFilter);

        const matchesCountry =
          countryFilter === "all" ||
          getCountryCandidates(attendee).some(
            (c) => normalizeOptionValue(c) === normalizeOptionValue(countryFilter)
          );

        const matchesLocation =
          locationFilter === "all" ||
          getLocationCandidates(attendee).some(
            (l) => normalizeOptionValue(l) === normalizeOptionValue(locationFilter)
          );

        return (
          matchesSearch &&
          matchesPaymentStatus &&
          matchesVerification &&
          matchesRegistrationType &&
          matchesTicketType &&
          matchesCountry &&
          matchesLocation
        );
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = `${a.user?.first_name || ""} ${a.user?.last_name || ""}`.toLowerCase();
            bValue = `${b.user?.first_name || ""} ${b.user?.last_name || ""}`.toLowerCase();
            break;
          case "email":
            aValue = (a.user?.email || "").toLowerCase();
            bValue = (b.user?.email || "").toLowerCase();
            break;
          case "location":
            aValue = (a.user?.location || "").toLowerCase();
            bValue = (b.user?.location || "").toLowerCase();
            break;
          case "verification":
            aValue = a.user?.email_verified_at || a.user?.phone_number_verified_at ? 1 : 0;
            bValue = b.user?.email_verified_at || b.user?.phone_number_verified_at ? 1 : 0;
            break;
          default:
            aValue = a[sortBy] || "";
            bValue = b[sortBy] || "";
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
  }, [
    attendees,
    searchTerm,
    paymentStatusFilter,
    verificationFilter,
    registrationTypeFilter,
    ticketTypeFilter,
    countryFilter,
    locationFilter,
    sortBy,
    sortOrder,
    normalizeOptionValue,
    getRegistrationTypeValue,
    getTicketTypeNameValue,
    getCountryCandidates,
    getLocationCandidates,
    getPaymentStatus,
  ]);

  // Pagination
  const paginatedAttendees = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAttendees.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAttendees, currentPage, itemsPerPage]);

  const filteredAttendeeCount = useMemo(
    () => filteredAttendees.reduce((sum, attendee) => sum + registrationHeadcount(attendee), 0),
    [filteredAttendees]
  );

  const totalPages = Math.ceil(filteredAttendees.length / itemsPerPage);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count += 1;
    if (paymentStatusFilter !== "all") count += 1;
    if (verificationFilter !== "all") count += 1;
    if (registrationTypeFilter !== "all") count += 1;
    if (ticketTypeFilter !== "all") count += 1;
    if (countryFilter !== "all") count += 1;
    if (locationFilter !== "all") count += 1;
    if (sortBy !== "name") count += 1;
    if (sortOrder !== "asc") count += 1;
    return count;
  }, [
    searchTerm,
    paymentStatusFilter,
    verificationFilter,
    registrationTypeFilter,
    ticketTypeFilter,
    countryFilter,
    locationFilter,
    sortBy,
    sortOrder,
  ]);

  // Handlers
  const handleViewAttendee = (attendee) => {
    setSelectedAttendee(attendee);
    setShowViewModal(true);
  };

  const handleEditAttendee = (attendee) => {
    setActionAttendee(attendee);
    setShowViewModal(false);
    setSelectedAttendee(null);
    setShowEditModal(true);
  };

  const handlePaymentConfirm = (attendee) => {
    if (!attendee) return;

    const paymentStatus = getPaymentStatus(attendee);
    if (paymentStatus !== "pending") {
      toast.info("Payment is already confirmed for this registration.");
      return;
    }

    setActionAttendee(attendee);
    setShowPaymentConfirmModal(true);
  };

  const handleSaveGroupEdit = async (payload) => {
    if (!actionAttendee) return;

    const attendeeSlug =
      actionAttendee?.slug || actionAttendee?.id || actionAttendee?.user_slug;
    if (!attendeeSlug) {
      toast.error("Unable to edit attendee: missing attendee slug.");
      return;
    }

    try {
      setEditSavingSlug(attendeeSlug);
      const response = await eventService.updateAttendeeRegistration(
        attendeeSlug,
        payload || {}
      );

      if (response?.data?.status_code === 200 || response?.status === true) {
        toast.success(
          response?.data?.reason ||
            response?.message ||
            "Edit request submitted."
        );
        const eventSlug = selectedEvent?.slug || selectedEvent?.event_slug;
        if (eventSlug) fetchAttendees(eventSlug);
        setShowEditModal(false);
        setActionAttendee(null);
      } else {
        toast.error(
          response?.data?.reason ||
            response?.message ||
            "Failed to submit edit request."
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.reason ||
          error.message ||
          "Failed to submit edit request."
      );
    } finally {
      setEditSavingSlug(null);
    }
  };

  const handleConfirmPaymentConfirm = async () => {
    if (!actionAttendee) return;

    const attendeeSlug =
      actionAttendee?.slug || actionAttendee?.id || actionAttendee?.user_slug;
    if (!attendeeSlug) {
      toast.error("Unable to confirm payment: missing attendee slug.");
      return;
    }

    try {
      setPaymentConfirmingSlug(attendeeSlug);
      const response = await eventService.markAttendeeAsPaid(attendeeSlug);

      if (response?.data?.status_code === 200 || response?.status === true) {
        toast.success(
          response?.data?.reason ||
            response?.message ||
            "Payment confirmed. Invitation email should be sent."
        );
        const eventSlug = selectedEvent?.slug || selectedEvent?.event_slug;
        if (eventSlug) fetchAttendees(eventSlug);
        setShowPaymentConfirmModal(false);
        setActionAttendee(null);
      } else {
        toast.error(response?.data?.reason || response?.message || "Failed to confirm payment.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.reason ||
          error.message ||
          "Failed to confirm payment."
      );
    } finally {
      setPaymentConfirmingSlug(null);
    }
  };

  const modalUser = selectedAttendee?.user || {};
  const modalRegistrationTypeLabel = selectedAttendee
    ? getRegistrationTypeLabel(selectedAttendee)
    : "N/A";
  const modalRegistrationTypeBadgeClasses = selectedAttendee
    ? getRegistrationTypeBadgeClasses(selectedAttendee)
    : "bg-gray-100 text-gray-700 border-gray-200";
  const modalTicketType = selectedAttendee
    ? getTicketTypeDetails(selectedAttendee)
    : { name: "N/A", price: "Price N/A" };
  const modalIsGroupRegistration =
    getRegistrationTypeValue(selectedAttendee) === "group";
  const modalFullName = [
    selectedAttendee?.first_name ||
      modalUser.first_name ||
      selectedAttendee?.primary_contact_details?.primary_contact_name ||
      selectedAttendee?.group_info?.primary_contact_name,
    selectedAttendee?.last_name || modalUser.last_name,
  ]
    .filter(Boolean)
    .join(" ");
  const modalEmail =
    selectedAttendee?.email ||
    modalUser.email ||
    selectedAttendee?.primary_contact_details?.primary_contact_email ||
    selectedAttendee?.group_info?.primary_contact_email ||
    "N/A";
  const modalPhone =
    selectedAttendee?.contact_number ||
    modalUser.phone_number ||
    selectedAttendee?.primary_contact_details?.primary_contact_phone ||
    selectedAttendee?.group_info?.primary_contact_phone ||
    "N/A";
  const modalGender =
    selectedAttendee?.gender || modalUser.gender || "N/A";
  const modalDob =
    selectedAttendee?.date_of_birth || modalUser.dob || null;
  const modalLocation =
    selectedAttendee?.group_info?.city ||
    selectedAttendee?.departure_city ||
    modalUser.location ||
    "N/A";
  const modalRegion = modalUser.region || "N/A";
  const modalCountry =
    selectedAttendee?.group_info?.country ||
    selectedAttendee?.country_of_residence ||
    modalUser.country ||
    "N/A";
  const modalGroupAttendees = Array.isArray(selectedAttendee?.attendees)
    ? selectedAttendee.attendees
    : [];

  const handleBack = () => {
    navigate("/admin/media/conferences");
  };

  // Get verification status badge
  const getVerificationBadge = (user) => {
    if (user?.email_verified_at || user?.phone_number_verified_at) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Verified
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <AlertCircle className="w-3 h-3 mr-1" />
        Unverified
      </span>
    );
  };

  // Get payment status badge
  const getPaymentStatusBadge = useCallback(
    (attendee) => {
      const status = getPaymentStatus(attendee);
      if (status === "free") {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Paid (Free)
          </span>
        );
      }
      if (status === "pending") {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 whitespace-nowrap">
            <Clock className="w-3 h-3 mr-1" />
            Pending Payment
          </span>
        );
      }

      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Payment Confirmed
        </span>
      );
    },
    [getPaymentStatus]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader fullScreen={true} text="Loading conferences..." />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 py-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 flex items-center">
              <GraduationCap className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 mr-2 sm:mr-3 text-blue-600 flex-shrink-0" />
              <span className="truncate">Conference Attendees</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Manage attendees for conferences
            </p>
          </div>

          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={() => {
                fetchEvents();
                if (selectedEvent) {
                  fetchAttendees(selectedEvent.slug || selectedEvent.event_slug);
                }
              }}
              className="bg-gray-100 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <RefreshCw className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleBack}
              className="bg-white text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <ArrowLeft className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>Back</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 lg:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Total Attendees
                </p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600 mt-1">
                  {stats.totalAttendees}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0 ml-3">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 lg:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Total Revenue
                </p>
                {stats.revenueByCurrency.length === 0 ? (
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600 mt-1">0.00</p>
                ) : (
                  <div className="mt-1 space-y-1">
                    {stats.revenueByCurrency.map(({ currency, amount }) => (
                      <p
                        key={currency}
                        className={`font-bold text-green-600 leading-tight ${
                          stats.revenueByCurrency.length > 1
                            ? "text-base sm:text-lg"
                            : "text-xl sm:text-2xl lg:text-3xl"
                        }`}
                      >
                        {formatMoney(amount, currency === "Unspecified" ? "" : currency)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-2 sm:p-3 bg-green-100 rounded-lg flex-shrink-0 ml-3">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 lg:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Paid / Pending
                </p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-purple-600 mt-1">
                  {stats.paidCount} / {stats.pendingCount}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.paidCount} paid, {stats.pendingCount} pending
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-purple-100 rounded-lg flex-shrink-0 ml-3">
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Event Selector & Filters */}
        <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
          {/* Event Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Conference
            </label>
            <select
              value={selectedEvent?.slug || selectedEvent?.event_slug || ""}
              onChange={(e) => {
                const event = events.find(
                  (ev) => (ev.slug || ev.event_slug) === e.target.value
                );
                setSelectedEvent(event);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {events.map((event) => (
                <option key={event.slug || event.event_slug} value={event.slug || event.event_slug}>
                  {event.event_title || event.venue_name}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50/60 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Filter className="w-4 h-4 text-blue-600" />
                  Filters & Search
                </h3>
                <p className="text-xs text-slate-600 mt-1">
                  {activeFilterCount > 0
                    ? `${activeFilterCount} active filters applied`
                    : "No filters applied"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowFiltersDrawer(true)}
                className="shrink-0 px-4 py-2.5 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Open Filters
              </button>
            </div>
          </div>
        </div>

        {/* Attendees Display */}
      {attendeesLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader text="Loading attendees..." />
        </div>
      ) : (
        <>
          {/* Attendees Table/Cards */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Attendee List ({filteredAttendeeCount}{" "}
                  {filteredAttendeeCount === 1 ? "attendee" : "attendees"})
                </h3>
                <div className="hidden sm:block text-xs sm:text-sm text-gray-500">
                  Page {currentPage} of {totalPages || 1}
                </div>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Attendee Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Contact Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Registration Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Ticket Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Amount Paid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Verification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedAttendees.map((attendee, index) => {
                    const user = attendee.user || {};
                    const registrationTypeLabel = getRegistrationTypeLabel(attendee);
                    const ticketType = getTicketTypeDetails(attendee);
                    const paymentStatus = getPaymentStatus(attendee);
                    const attendeeSlug =
                      attendee?.slug || attendee?.id || attendee?.user_slug || "";
                    const isGroupRegistration =
                      getRegistrationTypeValue(attendee) === "group";
                    return (
                      <tr
                        key={attendee.user_slug || index}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                              <span className="text-white font-medium text-sm">
                                {user.first_name?.charAt(0) || ""}
                                {user.last_name?.charAt(0) || ""}
                              </span>
                            </div>
                            <div className="ml-4 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center truncate">
                                {user.gender && (
                                  <>
                                    <span className="w-2 h-2 bg-gray-300 rounded-full mr-2 flex-shrink-0"></span>
                                    {user.gender.charAt(0).toUpperCase() +
                                      user.gender.slice(1)}
                                  </>
                                )}
                                {user.dob && (
                                  <span className="ml-2 text-gray-400 truncate">
                                    • DOB: {formatDob(user.dob)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="text-sm text-gray-900 flex items-center truncate">
                              <Mail className="w-3 h-3 mr-2 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{user.email || "N/A"}</span>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center truncate">
                              <Phone className="w-3 h-3 mr-2 text-gray-400 flex-shrink-0" />
                              <span className="truncate">
                                {user.phone_number || "N/A"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center min-w-0">
                            <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {user.location || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {user.region || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getRegistrationTypeBadgeClasses(
                              attendee
                            )}`}
                          >
                            {registrationTypeLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="inline-flex flex-col px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 min-w-[140px]">
                            <span className="text-xs font-semibold text-gray-900 truncate">
                              {ticketType.name}
                            </span>
                            <span className="text-xs text-blue-700 font-medium">
                              {ticketType.price}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatAmountPaid(attendee)}
                          </span>
                        </td>
                        <td className="px-6 py-4 align-top">
                          {getVerificationBadge(user)}
                          <div className="text-xs text-gray-500 mt-1 whitespace-nowrap">
                            Email {user.email_verified_at ? "✓" : "✗"} | Phone{" "}
                            {user.phone_number_verified_at ? "✓" : "✗"}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          {getPaymentStatusBadge(attendee)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleViewAttendee(attendee)}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors hover:bg-blue-50 rounded-lg"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>

                            {isGroupRegistration && (
                              <button
                                onClick={() => handleEditAttendee(attendee)}
                                className="p-2 text-gray-400 hover:text-amber-600 transition-colors hover:bg-amber-50 rounded-lg"
                                title="Edit Attendee"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}

                            <button
                              onClick={() => handlePaymentConfirm(attendee)}
                              disabled={
                                paymentStatus !== "pending" ||
                                paymentConfirmingSlug === attendeeSlug
                              }
                              className={`p-2 rounded-lg transition-colors ${
                                paymentStatus === "pending"
                                  ? "text-gray-400 hover:text-emerald-700 hover:bg-emerald-50"
                                  : "text-gray-300 cursor-not-allowed bg-gray-50"
                              }`}
                              title="Payment Confirm"
                            >
                              {paymentConfirmingSlug === attendeeSlug ? (
                                <RefreshCw className="w-4 h-4 animate-spin text-emerald-600" />
                              ) : (
                                <CreditCard className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {paginatedAttendees.map((attendee, index) => {
                const user = attendee.user || {};
                const registrationTypeLabel = getRegistrationTypeLabel(attendee);
                const ticketType = getTicketTypeDetails(attendee);
                const paymentStatus = getPaymentStatus(attendee);
                const attendeeSlug =
                  attendee?.slug || attendee?.id || attendee?.user_slug || "";
                const isGroupRegistration =
                  getRegistrationTypeValue(attendee) === "group";
                return (
                  <div
                    key={attendee.user_slug || index}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-sm mr-3">
                          <span className="text-white font-medium text-sm">
                            {user.first_name?.charAt(0) || ""}
                            {user.last_name?.charAt(0) || ""}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-base font-semibold text-gray-900 truncate">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {getVerificationBadge(user)}
                            {getPaymentStatusBadge(attendee)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <button
                          onClick={() => handleViewAttendee(attendee)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors hover:bg-blue-50 rounded-lg"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {isGroupRegistration && (
                          <button
                            onClick={() => handleEditAttendee(attendee)}
                            className="p-2 text-gray-400 hover:text-amber-600 transition-colors hover:bg-amber-50 rounded-lg"
                            title="Edit Attendee"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() => handlePaymentConfirm(attendee)}
                          disabled={
                            paymentStatus !== "pending" ||
                            paymentConfirmingSlug === attendeeSlug
                          }
                          className={`p-2 rounded-lg transition-colors ${
                            paymentStatus === "pending"
                              ? "text-gray-400 hover:text-emerald-700 hover:bg-emerald-50"
                              : "text-gray-300 cursor-not-allowed bg-gray-50"
                          }`}
                          title="Payment Confirm"
                        >
                          {paymentConfirmingSlug === attendeeSlug ? (
                            <RefreshCw className="w-4 h-4 animate-spin text-emerald-600" />
                          ) : (
                            <CreditCard className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      {/* Contact Info */}
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <Mail className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <span className="text-gray-900 truncate">
                            {user.email || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <span className="text-gray-900 truncate">
                            {user.phone_number || "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-center text-sm">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-gray-900 truncate">
                          {user.location || "N/A"}, {user.region || "N/A"}
                        </span>
                      </div>

                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 mr-2">Registration:</span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getRegistrationTypeBadgeClasses(
                            attendee
                          )}`}
                        >
                          {registrationTypeLabel}
                        </span>
                      </div>

                      <div className="flex items-start text-sm">
                        <Ticket className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="inline-flex flex-col px-2.5 py-1 rounded-lg border border-gray-200 bg-gray-50 min-w-[140px]">
                          <span className="text-xs font-semibold text-gray-900 truncate">
                            {ticketType.name}
                          </span>
                          <span className="text-xs text-blue-700 font-medium">
                            {ticketType.price}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 mr-2">Amount paid:</span>
                        <span className="font-semibold text-gray-900">{formatAmountPaid(attendee)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {filteredAttendees.length === 0 && !attendeesLoading && (
              <div className="text-center py-12 lg:py-16">
                <Users className="mx-auto h-12 w-12 lg:h-16 lg:w-16 text-gray-300" />
                <h3 className="mt-4 text-base lg:text-lg font-medium text-gray-900">
                  No attendees found
                </h3>
                <p className="mt-2 text-sm lg:text-sm text-gray-500 max-w-sm mx-auto px-4">
                  {searchTerm ||
                  paymentStatusFilter !== "all" ||
                  verificationFilter !== "all" ||
                  registrationTypeFilter !== "all" ||
                  ticketTypeFilter !== "all" ||
                  countryFilter !== "all" ||
                  locationFilter !== "all"
                    ? "Try adjusting your search or filter criteria."
                    : selectedEvent
                    ? "No attendees registered for this conference yet."
                    : "Please select a conference to view attendees."}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredAttendees.length > 0 && (
            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
                <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredAttendees.length)}{" "}
                  of {filteredAttendees.length} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg font-medium">
                    {currentPage}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* View attendee drawer */}
      <AnimatePresence>
        {showViewModal && selectedAttendee && (
          <div className="fixed inset-0 z-50">
            <motion.div
              className="fixed inset-0 bg-slate-900/50"
              onClick={() => {
                setShowViewModal(false);
                setSelectedAttendee(null);
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <motion.aside
              className="fixed right-0 top-0 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", ease: "easeOut", duration: 0.28 }}
            >
              {/* Header */}
              <div className="flex-shrink-0 bg-gradient-to-br from-blue-600 to-indigo-700 px-6 py-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-4">
                    {/* Avatar */}
                    {(!modalIsGroupRegistration && selectedAttendee?.profile_photo) ||
                    (modalIsGroupRegistration && selectedAttendee?.primary_contact_details?.profile_photo) ? (
                      <img
                        src={
                          modalIsGroupRegistration
                            ? selectedAttendee.primary_contact_details.profile_photo
                            : selectedAttendee.profile_photo
                        }
                        alt=""
                        className="h-14 w-14 flex-shrink-0 rounded-xl object-cover ring-2 ring-white/40"
                      />
                    ) : (
                      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-white/20 text-lg font-bold text-white">
                        {(modalIsGroupRegistration
                          ? selectedAttendee?.group_info?.organization_name || "G"
                          : modalFullName || "A"
                        )
                          .split(" ")
                          .filter(Boolean)
                          .map((w) => w[0])
                          .join("")
                          .substring(0, 2)
                          .toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="truncate text-xl font-bold text-white">
                        {modalIsGroupRegistration
                          ? selectedAttendee?.group_info?.organization_name || "Group registration"
                          : modalFullName || "Attendee Details"}
                      </h3>
                      <p className="mt-0.5 truncate text-sm text-blue-100">
                        {selectedEvent?.event_title || "Conference"}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center rounded-full border border-white/30 bg-white/15 px-2.5 py-0.5 text-xs font-semibold text-white">
                          {modalRegistrationTypeLabel}
                        </span>
                        {getPaymentStatusBadge(selectedAttendee)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowViewModal(false); setSelectedAttendee(null); }}
                    className="flex-shrink-0 rounded-lg p-2 text-white hover:bg-white/20"
                    aria-label="Close drawer"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto">
                <div className="space-y-0 divide-y divide-gray-100">

                  {/* ── SINGLE: Personal ───────────────────────────── */}
                  {!modalIsGroupRegistration && (
                    <section className="px-6 py-5">
                      <p className="mb-4 flex items-center text-xs font-bold uppercase tracking-widest text-blue-600">
                        <User className="mr-2 h-3.5 w-3.5" /> Personal
                      </p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <div className="col-span-2 flex items-center gap-4">
                          {selectedAttendee?.profile_photo && (
                            <a href={selectedAttendee.profile_photo} target="_blank" rel="noreferrer">
                              <img src={selectedAttendee.profile_photo} alt="" className="h-20 w-20 rounded-xl border border-gray-200 object-cover" />
                            </a>
                          )}
                          <div>
                            <p className="text-xs text-gray-500">Full name</p>
                            <p className="text-sm font-semibold text-gray-900">{modalFullName || "N/A"}</p>
                            {modalGender !== "N/A" && <p className="mt-0.5 text-xs capitalize text-gray-500">{modalGender}</p>}
                          </div>
                        </div>
                        {modalDob && (
                          <div>
                            <p className="text-xs text-gray-500">Date of birth</p>
                            <p className="text-sm font-semibold text-gray-900">{formatDob(modalDob)}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-500">Nationality</p>
                          <p className="text-sm font-semibold text-gray-900">{selectedAttendee.nationality || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Profession</p>
                          <p className="text-sm font-semibold text-gray-900">{selectedAttendee.profession || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Organization</p>
                          <p className="text-sm font-semibold text-gray-900">{selectedAttendee.organization || "N/A"}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500">Address</p>
                          <p className="text-sm font-semibold text-gray-900">{selectedAttendee.address || "N/A"}</p>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── SINGLE: Contact ─────────────────────────────── */}
                  {!modalIsGroupRegistration && (
                    <section className="px-6 py-5">
                      <p className="mb-4 flex items-center text-xs font-bold uppercase tracking-widest text-blue-600">
                        <Mail className="mr-2 h-3.5 w-3.5" /> Contact
                      </p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="text-sm font-semibold text-gray-900 break-all">{modalEmail}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="text-sm font-semibold text-gray-900">{modalPhone}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Departure city</p>
                          <p className="text-sm font-semibold text-gray-900">{selectedAttendee.departure_city || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Country</p>
                          <p className="text-sm font-semibold text-gray-900">{modalCountry}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Region / Location</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {[modalLocation !== "N/A" ? modalLocation : null, modalRegion !== "N/A" ? modalRegion : null].filter(Boolean).join(", ") || "N/A"}
                          </p>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── GROUP: Organization & Primary contact ──────── */}
                  {modalIsGroupRegistration && (
                    <section className="px-6 py-5">
                      <p className="mb-4 flex items-center text-xs font-bold uppercase tracking-widest text-blue-600">
                        <Building2 className="mr-2 h-3.5 w-3.5" /> Group Details
                      </p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500">Organization</p>
                          <p className="text-sm font-semibold text-gray-900">{selectedAttendee?.group_info?.organization_name || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">City</p>
                          <p className="text-sm font-semibold text-gray-900">{selectedAttendee?.group_info?.city || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Country</p>
                          <p className="text-sm font-semibold text-gray-900">{selectedAttendee?.group_info?.country || "N/A"}</p>
                        </div>
                      </div>

                      {/* Primary contact */}
                      <div className="mt-5 rounded-xl bg-blue-50 p-4 border border-blue-100">
                        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-blue-700">Primary contact</p>
                        <div className="flex items-start gap-4">
                          {selectedAttendee?.primary_contact_details?.profile_photo && (
                            <a href={selectedAttendee.primary_contact_details.profile_photo} target="_blank" rel="noreferrer">
                              <img src={selectedAttendee.primary_contact_details.profile_photo} alt="" className="h-16 w-16 rounded-xl border border-blue-200 object-cover" />
                            </a>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                            <div>
                              <p className="text-xs text-blue-600">Name</p>
                              <p className="text-sm font-semibold text-gray-900">{selectedAttendee?.group_info?.primary_contact_name || "N/A"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-blue-600">Phone</p>
                              <p className="text-sm font-semibold text-gray-900">{selectedAttendee?.group_info?.primary_contact_phone || "N/A"}</p>
                            </div>
                            <div className="sm:col-span-2">
                              <p className="text-xs text-blue-600">Email</p>
                              <p className="text-sm font-semibold text-gray-900 break-all">{selectedAttendee?.group_info?.primary_contact_email || "N/A"}</p>
                            </div>
                          </div>
                        </div>
                        {selectedAttendee?.group_info?.primary_contact_signature && (
                          <div className="mt-3">
                            <p className="text-xs text-blue-600 mb-1">Signature</p>
                            <img
                              src={selectedAttendee.group_info.primary_contact_signature}
                              alt="Signature"
                              className="h-20 w-full max-w-xs rounded-lg border border-blue-200 bg-white object-contain"
                            />
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* ── Ticket & Payment ────────────────────────────── */}
                  <section className="px-6 py-5">
                    <p className="mb-4 flex items-center text-xs font-bold uppercase tracking-widest text-blue-600">
                      <Ticket className="mr-2 h-3.5 w-3.5" /> Ticket &amp; Payment
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      <div className="col-span-2 flex items-stretch gap-3">
                        <div className="flex-1 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                          <p className="text-xs text-gray-500">Ticket type</p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900">{modalTicketType.name}</p>
                          <p className="mt-0.5 text-xs font-medium text-blue-700">{modalTicketType.price}</p>
                        </div>
                        <div className="flex-1 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                          <p className="text-xs text-gray-500">Amount paid</p>
                          <p className="mt-0.5 text-lg font-bold text-gray-900">{formatAmountPaid(selectedAttendee)}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Payment status</p>
                        <div className="mt-1">{getPaymentStatusBadge(selectedAttendee)}</div>
                        <p className="mt-1.5 text-xs text-gray-500">
                          {getPaymentStatus(selectedAttendee) === "free"
                            ? "Package is free — marked as paid."
                            : getPaymentStatus(selectedAttendee) === "pending"
                            ? "Awaiting payment confirmation."
                            : "Payment confirmed."}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Agent code</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">{selectedAttendee.agent_code || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Registration type</p>
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${modalRegistrationTypeBadgeClasses}`}>
                            {modalRegistrationTypeLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* ── Account ─────────────────────────────────────── */}
                  <section className="px-6 py-5">
                    <p className="mb-4 flex items-center text-xs font-bold uppercase tracking-widest text-blue-600">
                      <User className="mr-2 h-3.5 w-3.5" /> Account
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      <div>
                        <p className="text-xs text-gray-500">Registered name</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {[modalUser.first_name, modalUser.last_name].filter(Boolean).join(" ") || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Channel</p>
                        <span className="mt-1 inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium capitalize text-blue-800">
                          {modalUser.channel || "N/A"}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="text-sm font-semibold text-gray-900 break-all">{modalUser.email || "N/A"}</p>
                      </div>
                      {modalUser?.document_type && (
                        <>
                          <div>
                            <p className="text-xs text-gray-500">Document type</p>
                            <p className="text-sm font-semibold text-gray-900 capitalize">{String(modalUser.document_type).replace(/_/g, " ")}</p>
                          </div>
                          {modalUser.card_number && (
                            <div>
                              <p className="text-xs text-gray-500">Card / ID number</p>
                              <p className="text-sm font-mono font-semibold text-gray-900">{modalUser.card_number}</p>
                            </div>
                          )}
                          {(modalUser.id_card_front || modalUser.id_card_back) && (
                            <div className="col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                              {modalUser.id_card_front && (
                                <a href={modalUser.id_card_front} target="_blank" rel="noreferrer" className="block">
                                  <div className="flex h-72 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-2">
                                    <img src={modalUser.id_card_front} alt="ID front" className="max-h-full max-w-full object-contain" />
                                  </div>
                                  <p className="mt-1 text-center text-xs text-gray-400">Front</p>
                                </a>
                              )}
                              {modalUser.id_card_back && (
                                <a href={modalUser.id_card_back} target="_blank" rel="noreferrer" className="block">
                                  <div className="flex h-72 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-2">
                                    <img src={modalUser.id_card_back} alt="ID back" className="max-h-full max-w-full object-contain" />
                                  </div>
                                  <p className="mt-1 text-center text-xs text-gray-400">Back</p>
                                </a>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </section>

                  {/* ── GROUP: Member list ───────────────────────────── */}
                  {modalIsGroupRegistration && (
                    <section className="px-6 py-5">
                      <p className="mb-4 flex items-center text-xs font-bold uppercase tracking-widest text-blue-600">
                        <Users className="mr-2 h-3.5 w-3.5" /> Members ({modalGroupAttendees.length})
                      </p>
                      {modalGroupAttendees.length === 0 ? (
                        <p className="text-sm text-gray-500">No member list provided.</p>
                      ) : (
                        <div className="space-y-4">
                          {modalGroupAttendees.map((member, idx) => {
                            const memberName = [member.first_name, member.last_name].filter(Boolean).join(" ") || `Member ${idx + 1}`;
                            return (
                              <details key={`${member.email || memberName}-${idx}`} className="group rounded-xl border border-gray-200 bg-white overflow-hidden">
                                <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 list-none">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-sm font-bold text-blue-700">
                                      {memberName.split(" ").filter(Boolean).map((w) => w[0]).join("").substring(0, 2).toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-semibold text-gray-900 truncate">{memberName}</p>
                                      <p className="text-xs text-gray-500 truncate">{member.email || "No email"}</p>
                                    </div>
                                  </div>
                                  <span className="flex-shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 border border-blue-100">#{idx + 1}</span>
                                </summary>
                                <div className="border-t border-gray-100 px-4 py-4 space-y-4">
                                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                                    <div>
                                      <p className="text-xs text-gray-500">Phone</p>
                                      <p className="text-sm font-semibold text-gray-900">{member.contact_number || "N/A"}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Gender</p>
                                      <p className="text-sm font-semibold text-gray-900 capitalize">{member.gender || "N/A"}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Nationality</p>
                                      <p className="text-sm font-semibold text-gray-900">{member.nationality || "N/A"}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Country of residence</p>
                                      <p className="text-sm font-semibold text-gray-900">{member.country_of_residence || "N/A"}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Profession</p>
                                      <p className="text-sm font-semibold text-gray-900">{member.profession || "N/A"}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Organization</p>
                                      <p className="text-sm font-semibold text-gray-900">{member.organization || "N/A"}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Departure city</p>
                                      <p className="text-sm font-semibold text-gray-900">{member.departure_city || "N/A"}</p>
                                    </div>
                                    <div className="col-span-2">
                                      <p className="text-xs text-gray-500">Address</p>
                                      <p className="text-sm font-semibold text-gray-900">{member.address || "N/A"}</p>
                                    </div>
                                  </div>
                                  {member.id_card_front && (
                                    <a href={member.id_card_front} target="_blank" rel="noreferrer" className="block">
                                      <div className="flex h-72 w-full max-w-md items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-2">
                                        <img src={member.id_card_front} alt="ID card" className="max-h-full max-w-full object-contain" />
                                      </div>
                                    </a>
                                  )}
                                  {(member.motivation_answers?.length > 0 || member.motivational_answers?.length > 0) && (
                                    <div>
                                      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-blue-600">Responses</p>
                                      <MotivationAnswers answers={member.motivation_answers || member.motivational_answers} />
                                    </div>
                                  )}
                                </div>
                              </details>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  )}

                  {/* ── SINGLE: Motivational answers ────────────────── */}
                  {!modalIsGroupRegistration && (
                    <section className="px-6 py-5">
                      <p className="mb-4 flex items-center text-xs font-bold uppercase tracking-widest text-blue-600">
                        <FileText className="mr-2 h-3.5 w-3.5" /> Motivational Answers
                      </p>
                      <MotivationAnswers
                        answers={selectedAttendee.motivation_answers || selectedAttendee.motivational_answers}
                      />
                    </section>
                  )}

                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedAttendee(null);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

      <ConferenceAttendeeFiltersDrawer
        isOpen={showFiltersDrawer}
        onClose={() => setShowFiltersDrawer(false)}
        searchTerm={searchTerm}
        paymentStatusFilter={paymentStatusFilter}
        verificationFilter={verificationFilter}
        registrationTypeFilter={registrationTypeFilter}
        ticketTypeFilter={ticketTypeFilter}
        countryFilter={countryFilter}
        locationFilter={locationFilter}
        sortBy={sortBy}
        sortOrder={sortOrder}
        registrationTypeOptions={registrationTypeOptions}
        ticketTypeOptions={ticketTypeOptions}
        countryOptions={countryOptions}
        locationOptions={locationOptions}
        onSearchChange={(value) => {
          setSearchTerm(value);
          setCurrentPage(1);
        }}
        onPaymentStatusChange={(value) => {
          setPaymentStatusFilter(value);
          setCurrentPage(1);
        }}
        onVerificationChange={(value) => {
          setVerificationFilter(value);
          setCurrentPage(1);
        }}
        onRegistrationTypeChange={(value) => {
          setRegistrationTypeFilter(value);
          setCurrentPage(1);
        }}
        onTicketTypeChange={(value) => {
          setTicketTypeFilter(value);
          setCurrentPage(1);
        }}
        onCountryChange={(value) => {
          setCountryFilter(value);
          setCurrentPage(1);
        }}
        onLocationChange={(value) => {
          setLocationFilter(value);
          setCurrentPage(1);
        }}
        onSortByChange={(value) => {
          setSortBy(value);
          setCurrentPage(1);
        }}
        onSortOrderToggle={() => {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
          setCurrentPage(1);
        }}
        onClearFilters={() => {
          setSearchTerm("");
          setPaymentStatusFilter("all");
          setVerificationFilter("all");
          setRegistrationTypeFilter("all");
          setTicketTypeFilter("all");
          setCountryFilter("all");
          setLocationFilter("all");
          setSortBy("name");
          setSortOrder("asc");
          setCurrentPage(1);
        }}
        activeFilterCount={activeFilterCount}
      />

      <EditGroupAttendeeModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setActionAttendee(null);
          setEditSavingSlug(null);
        }}
        onSave={handleSaveGroupEdit}
        attendee={actionAttendee}
        initialFormData={{
          groupOrganizationName: actionAttendee?.group_info?.organization_name || "",
          groupCountry: actionAttendee?.group_info?.country || "",
          groupCity: actionAttendee?.group_info?.city || "",
          primaryContactName:
            actionAttendee?.primary_contact_details?.primary_contact_name || "",
          primaryContactPhone:
            actionAttendee?.primary_contact_details?.primary_contact_phone || "",
          primaryContactEmail:
            actionAttendee?.primary_contact_details?.primary_contact_email || "",
          attendees: Array.isArray(actionAttendee?.attendees)
            ? actionAttendee.attendees
            : [],
        }}
        isLoading={editSavingSlug !== null}
      />

      <ConfirmPaymentConfirmModal
        isOpen={showPaymentConfirmModal}
        onClose={() => {
          setShowPaymentConfirmModal(false);
          setActionAttendee(null);
          setPaymentConfirmingSlug(null);
        }}
        onConfirm={handleConfirmPaymentConfirm}
        attendee={actionAttendee}
        isLoading={paymentConfirmingSlug !== null}
      />

      </div>
    </div>
  );
};

export default ConferenceAttendees;